export const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatBudget = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value || "-";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatLeadStatus = (value) => (value ? String(value).replace(/_/g, " ") : "-");

const getFollowUpStatusSummary = (item) => {
  const previousStatus = item?.previous_status ? formatLeadStatus(item.previous_status) : "";
  const currentStatus = item?.status ? formatLeadStatus(item.status) : "";

  if (previousStatus && currentStatus && previousStatus !== currentStatus) {
    return `Status: ${previousStatus} -> ${currentStatus}`;
  }

  if (currentStatus) {
    return `Status: ${currentStatus}`;
  }

  if (previousStatus) {
    return `Previous Status: ${previousStatus}`;
  }

  return "";
};

const isFollowUpHistoryItem = (item) =>
  Boolean(
    item &&
      (item?.previous_status ||
        item?.next_follow_up_at ||
        Object.prototype.hasOwnProperty.call(item, "status")) &&
      (Object.prototype.hasOwnProperty.call(item, "note") ||
        Object.prototype.hasOwnProperty.call(item, "admin_name") ||
        Object.prototype.hasOwnProperty.call(item, "assigned_admin_name"))
  );

const buildFollowUpDescription = (item) =>
  [
    item?.note ? String(item.note).trim() : "",
    getFollowUpStatusSummary(item),
    item?.next_follow_up_at ? `Next Follow Up: ${formatDateTime(item.next_follow_up_at)}` : "",
  ]
    .filter(Boolean)
    .join("\n");

export const getLeadAssignee = (lead) =>
  lead?.assigned_admin_name ||
  lead?.assigned_staff_name ||
  lead?.assigned_to_name ||
  lead?.team_member_name ||
  lead?.team_name ||
  lead?.assigned_user_name ||
  lead?.assignee_name ||
  lead?.assignee ||
  "";

export const getLeadAssigneeId = (lead) =>
  lead?.assigned_admin_id ||
  lead?.assigned_staff_id ||
  lead?.assigned_to_id ||
  lead?.team_member_id ||
  lead?.team_id ||
  lead?.assigned_user_id ||
  lead?.assignee_id ||
  "";

export const getLeadAssignedBy = (lead) =>
  lead?.assigned_by_admin_name ||
  lead?.assigned_by_staff_name ||
  lead?.assigned_by_name ||
  lead?.assigned_by_user_name ||
  "";

export const getLeadAssignedById = (lead) =>
  lead?.assigned_by_admin_id ||
  lead?.assigned_by_staff_id ||
  lead?.assigned_by_id ||
  lead?.assigned_by_user_id ||
  "";

export const parseMaybeJson = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : parsed ? [parsed] : null;
  } catch (error) {
    return null;
  }
};

export const normalizeHistoryEntry = (item, index) => ({
  id: item?.id || item?.history_id || item?.remark_id || `${index}`,
  title:
    (isFollowUpHistoryItem(item)
      ? item?.status
        ? `Follow Up: ${formatLeadStatus(item.status)}`
        : "Follow Up"
      : item?.title ||
        item?.action ||
        item?.status ||
        item?.type ||
        item?.event ||
        item?.name) || "Follow up",
  description:
    (isFollowUpHistoryItem(item)
      ? buildFollowUpDescription(item)
      : item?.description ||
        item?.message ||
        item?.note ||
        item?.remark ||
        item?.text ||
        item?.comment ||
        item?.details) || "",
  timestamp:
    item?.timestamp ||
    item?.created_at ||
    item?.updated_at ||
    item?.date_time ||
    item?.createdAt ||
    "",
  assignedTo:
    item?.admin_name ||
    item?.assigned_admin_name ||
    item?.assigned_to_name ||
    item?.assigned_staff_name ||
    item?.team_member_name ||
    item?.user_name ||
    item?.staff_name ||
    "",
});

export const extractFollowHistory = (lead) => {
  if (Array.isArray(lead)) {
    return lead
      .map(normalizeHistoryEntry)
      .filter((entry) => entry.description || entry.title || entry.timestamp);
  }

  const resolvedLead = lead?.lead || lead;
  const candidates = [
    lead?.follow_ups,
    resolvedLead?.follow_ups,
    lead?.follow_history,
    lead?.follow_up_history,
    lead?.history,
    lead?.activity_log,
    lead?.remarks,
    lead?.remark,
    lead?.call_history,
    lead?.lead_history,
    resolvedLead?.follow_history,
    resolvedLead?.follow_up_history,
    resolvedLead?.history,
    resolvedLead?.activity_log,
    resolvedLead?.remarks,
    resolvedLead?.remark,
    resolvedLead?.call_history,
    resolvedLead?.lead_history,
  ];

  for (const candidate of candidates) {
    const parsed = parseMaybeJson(candidate);
    if (parsed && parsed.length > 0) {
      return parsed
        .map(normalizeHistoryEntry)
        .filter((entry) => entry.description || entry.title || entry.timestamp);
    }
  }

  const fallbackEvents = [];

  if (resolvedLead?.notes) {
    fallbackEvents.push({
      title: "Internal Note",
      description: String(resolvedLead.notes).trim(),
      timestamp: resolvedLead?.updated_at || resolvedLead?.created_at,
    });
  }

  if (
    resolvedLead?.status ||
    resolvedLead?.assigned_admin_name ||
    resolvedLead?.assigned_staff_name ||
    resolvedLead?.assigned_to_name ||
    resolvedLead?.team_name ||
    resolvedLead?.channel_partner_status
  ) {
    fallbackEvents.push({
      title: "Lead Snapshot",
      description: [
        resolvedLead?.status ? `Status: ${formatLeadStatus(resolvedLead.status)}` : "",
        resolvedLead?.channel_partner_status
          ? `Partner: ${formatLeadStatus(resolvedLead.channel_partner_status)}`
          : "",
        getLeadAssignee(resolvedLead) ? `Assigned To: ${getLeadAssignee(resolvedLead)}` : "",
      ]
        .filter(Boolean)
        .join(" | "),
      timestamp: resolvedLead?.updated_at || resolvedLead?.created_at,
    });
  }

  if (resolvedLead?.assigned_at || getLeadAssignee(resolvedLead)) {
    fallbackEvents.push({
      title: "Assigned",
      description: [
        getLeadAssignee(resolvedLead) ? `Assigned To: ${getLeadAssignee(resolvedLead)}` : "",
        getLeadAssignedBy(resolvedLead) ? `Assigned By: ${getLeadAssignedBy(resolvedLead)}` : "",
      ]
        .filter(Boolean)
        .join(" | "),
      timestamp: resolvedLead?.assigned_at || resolvedLead?.updated_at || resolvedLead?.created_at,
    });
  }

  if (resolvedLead?.last_follow_up_at) {
    fallbackEvents.push({
      title: "Last Follow Up",
      description: "A follow-up was recorded for this lead.",
      timestamp: resolvedLead.last_follow_up_at,
    });
  }

  if (resolvedLead?.created_at) {
    fallbackEvents.push({
      title: "Lead Created",
      description: "Lead added to the system.",
      timestamp: resolvedLead.created_at,
    });
  }

  return fallbackEvents;
};
